import { useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useGetResolutionRules,
  useUpsertResolutionRule,
} from "@/api/providers";
import { Provider, ResolutionMode } from "@/api.types.v2";
import { useQueryClient } from "@tanstack/react-query";
import {
  Settings,
  Plus,
  RefreshCw,
  Search,
  Info,
  Target,
  Crown,
  DollarSign,
} from "lucide-react";

interface ResolutionRuleDialogProps {
  open: boolean;
  onClose: () => void;
}

const ruleSchema = z.object({
  carrierCode: z.string().min(1, "Carrier code is required"),
  serviceCode: z.string().optional(),
  mode: z.nativeEnum(ResolutionMode),
  prefererCarrierId: z.string().optional(),
  note: z.string().optional(),
});

// Common carriers and their services
const CARRIERS = [
  {
    code: "UPS",
    name: "UPS",
    services: ["ups_ground", "ups_air", "ups_next_day"],
  },
  {
    code: "FEDEX",
    name: "FedEx",
    services: ["fedex_ground", "fedex_express", "fedex_overnight"],
  },
  {
    code: "USPS",
    name: "USPS",
    services: ["usps_priority", "usps_express", "usps_ground"],
  },
  {
    code: "CANADA_POST",
    name: "Canada Post",
    services: ["cp_regular", "cp_express", "cp_priority"],
  },
];

const RESOLUTION_MODE_INFO = {
  [ResolutionMode.SHOW_BOTH]: {
    icon: Target,
    color: "bg-blue-50 text-blue-700",
    description: "Show rates from both providers",
    example: "Display all available rates for user selection",
  },
  [ResolutionMode.PREFER_PROVIDER]: {
    icon: Crown,
    color: "bg-green-50 text-green-700",
    description: "Prefer specific provider if available, fallback to others",
    example: "Prefer ChitChats for UPS Ground, fallback to ShipStation",
  },
  [ResolutionMode.PREFER_LOWEST]: {
    icon: DollarSign,
    color: "bg-yellow-50 text-yellow-700",
    description: "Always select the lowest cost option",
    example: "Compare all provider rates and choose the cheapest",
  },
};

export function ResolutionRuleDialog({
  open,
  onClose,
}: ResolutionRuleDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();

  const { data: rulesData, isLoading } = useGetResolutionRules();
  const createMutation = useUpsertResolutionRule({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["resolution-rules"] });
      toast.success("Rule Created", {
        description: "Resolution rule has been created successfully.",
      });
      form.reset();
    },
    onError: () => {
      toast.error("Failed to create resolution rule.");
    },
  });

  const form = useForm<z.infer<typeof ruleSchema>>({
    resolver: zodResolver(ruleSchema),
    defaultValues: {
      carrierCode: "",
      serviceCode: "all",
      mode: ResolutionMode.SHOW_BOTH,
      prefererCarrierId: "",
      note: "",
    },
  });

  const selectedMode = form.watch("mode");
  const selectedCarrier = form.watch("carrierCode");

  const handleSubmit = (values: z.infer<typeof ruleSchema>) => {
    const payload = {
      carrierCode: values.carrierCode,
      mode: values.mode,
      serviceCode:
        values.serviceCode === "all" ? undefined : values.serviceCode,
      preferer:
        values.mode === ResolutionMode.PREFER_PROVIDER
          ? values.prefererCarrierId || undefined
          : undefined,
      note: values.note,
    };
    createMutation.mutate(
      payload as {
        carrierCode: string;
        serviceCode?: string;
        mode: ResolutionMode;
        preferer?: Provider;
        note?: string;
      }
    );
  };

  const filteredRules =
    rulesData?.data?.filter(
      (rule) =>
        rule.carrierCode.toLowerCase().includes(searchTerm.toLowerCase()) ??
        rule.serviceCode?.toLowerCase().includes(searchTerm.toLowerCase()) ??
        rule.mode.toLowerCase().includes(searchTerm.toLowerCase())
    ) ?? [];

  const getModeIcon = (mode: ResolutionMode) => {
    const IconComponent = RESOLUTION_MODE_INFO[mode]?.icon ?? Settings;
    return <IconComponent className="h-4 w-4" />;
  };

  const getModeColor = (mode: ResolutionMode) => {
    return RESOLUTION_MODE_INFO[mode]?.color ?? "bg-gray-50 text-gray-700";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Resolution Rules Configuration
          </DialogTitle>
          <DialogDescription>
            Configure how the system handles duplicate rates from multiple
            providers for the same carrier/service.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 flex-1 overflow-hidden">
          {/* Create New Rule Form */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Create New Rule</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="carrierCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Carrier</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select carrier" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {CARRIERS.map((carrier) => (
                              <SelectItem
                                key={carrier.code}
                                value={carrier.code}
                              >
                                {carrier.name} ({carrier.code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="serviceCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Service (Optional)</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="All services" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="all">All services</SelectItem>
                            {selectedCarrier &&
                              CARRIERS.find(
                                (c) => c.code === selectedCarrier
                              )?.services.map((service) => (
                                <SelectItem key={service} value={service}>
                                  {service.replace(/_/g, " ").toUpperCase()}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          Leave empty to apply to all services for this carrier
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Resolution Mode</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.entries(ResolutionMode).map(
                              ([key, mode]) => (
                                <SelectItem key={mode} value={mode}>
                                  <div className="flex items-center gap-2">
                                    {getModeIcon(mode)}
                                    {key.replace(/_/g, " ").toLowerCase()}
                                  </div>
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {selectedMode === ResolutionMode.PREFER_PROVIDER && (
                    <FormField
                      control={form.control}
                      name="prefererCarrierId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Preferred Provider</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select provider" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {Object.values(Provider).map((provider) => (
                                <SelectItem key={provider} value={provider}>
                                  {provider}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="note"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Note (Optional)</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Reason for this rule..."
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Explain why this rule exists for future reference
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {selectedMode && (
                    <Alert className="border-blue-200 bg-blue-50">
                      <Info className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-sm text-blue-800">
                        <strong>
                          {selectedMode.replace(/_/g, " ").toLowerCase()}:
                        </strong>{" "}
                        {RESOLUTION_MODE_INFO[selectedMode]?.description}
                      </AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={createMutation.isPending}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {createMutation.isPending ? "Creating..." : "Create Rule"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Existing Rules Table */}
          <Card className="lg:col-span-3 flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Active Rules</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search rules..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-[200px]"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      queryClient.invalidateQueries({
                        queryKey: ["resolution-rules"],
                      })
                    }
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Carrier/Service</TableHead>
                    <TableHead>Resolution Mode</TableHead>
                    <TableHead>Preferred Provider</TableHead>
                    <TableHead>Note</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredRules.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center py-8 text-gray-500"
                      >
                        {searchTerm
                          ? "No rules match your search"
                          : "No resolution rules configured"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">
                              {rule.carrierCode}
                            </div>
                            {rule.serviceCode && (
                              <Badge variant="outline" className="text-xs">
                                {rule.serviceCode}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getModeColor(rule.mode)}>
                            <div className="flex items-center gap-1">
                              {getModeIcon(rule.mode)}
                              {rule.mode.replace(/_/g, " ").toLowerCase()}
                            </div>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {rule.preferer && (
                            <Badge variant="secondary">{rule.preferer}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {rule.note && (
                            <span className="text-sm text-gray-600 truncate max-w-[200px] block">
                              {rule.note}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
